/*
  EXC FAQ page (sections/exc-faq-intro.liquid + sections/exc-faq-group.liquid).

  - Topic tiles: each tile links to #topic-<heading> and shows how many answers
    that topic holds, counted here from the questions on the page. Tapping one
    scrolls there smoothly (instantly with reduced motion).
  - Search: every word typed has to appear in the question or its answer (with
    a few spelling aliases, see ALIASES). While searching, the quick answers and
    the tiles step aside, topics with no match hide, and three matches or fewer
    open themselves (they close again when the search changes). ?q=<words> in the
    address opens the page already searched and stays in step as you type.
  - /pages/faq#q-<question> opens that question and scrolls to it.
*/

/* Words riders type that the answers spell differently. When the text contains
   the left side, the right side is added to what search matches. */
const ALIASES = [
  [/\bbled\b|pre-bled/, 'bleed bleeding prebled'],
  [/light bee x|\blbx\b/, 'lbx lightbee'],
  [/ultra bee/, 'ultrabee'],
  [/e ride|e-ride/, 'eride'],
  [/surron|sur-ron/, 'surron sur-ron'],
  [/supermoto/, 'super moto sumo'],
  [/shift lock/, 'shiftlock footlock'],
  [/foot brake|footbrake/, 'footbrake'],
  [/wheel ?set/, 'wheelset wheels'],
  [/\btires?\b/, 'tyre tyres'],
  [/titanium/, 'ti bolts'],
];

function withAliases(text) {
  let extra = '';
  ALIASES.forEach(([pattern, words]) => {
    if (pattern.test(text)) extra += ` ${words}`;
  });
  return text + extra;
}

function scrollToElement(el) {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
}

class ExcFaqSearch extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.input = this.querySelector('[data-exc-faq-input]');
    this.status = this.querySelector('[data-exc-faq-status]');
    this.none = this.querySelector('[data-exc-faq-none]');
    this.browse = Array.from(this.querySelectorAll('[data-exc-faq-browse]'));
    this.groups = Array.from(document.querySelectorAll('[data-exc-faq-group]'));
    this.items = [];
    this.groups.forEach((group) => {
      group.querySelectorAll('[data-exc-faq-q]').forEach((el) => {
        this.items.push({ el, group, text: withAliases(el.textContent.replace(/\s+/g, ' ').trim().toLowerCase()) });
      });
    });
    this.autoOpened = [];

    this.setUpTiles();

    if (this.input) {
      this.input.addEventListener('input', () => this.apply(this.input.value, true));
      const form = this.input.closest('form');
      if (form) {
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.input.blur();
        });
      }
      const initial = new URLSearchParams(window.location.search).get('q');
      if (initial) {
        this.input.value = initial;
        this.apply(initial, false);
      }
    }

    this.openFromHash();
    window.addEventListener('hashchange', () => this.openFromHash());
  }

  setUpTiles() {
    this.querySelectorAll('[data-exc-faq-tile]').forEach((tile) => {
      const group = document.getElementById(tile.dataset.excFaqTile);
      const count = tile.querySelector('[data-exc-faq-count]');
      if (!group) {
        // A tile whose topic is not on the page goes nowhere, so it hides.
        const item = tile.closest('li');
        if (item) item.hidden = true;
        return;
      }
      const n = group.querySelectorAll('[data-exc-faq-q]').length;
      if (n === 0) {
        const emptyItem = tile.closest('li');
        if (emptyItem) emptyItem.hidden = true;
        return;
      }
      if (count) count.textContent = n === 1 ? '1 answer' : `${n} answers`;
      tile.addEventListener('click', (event) => {
        event.preventDefault();
        scrollToElement(group);
        group.focus({ preventScroll: true });
        window.history.replaceState(window.history.state, '', `#${group.id}`);
      });
    });
  }

  apply(raw, updateUrl) {
    const query = (raw || '').trim().toLowerCase();
    const words = query.split(/\s+/).filter(Boolean);

    this.autoOpened.forEach((el) => {
      el.open = false;
    });
    this.autoOpened = [];

    let matches = 0;
    const matched = [];
    this.items.forEach((item) => {
      const hit = words.every((word) => item.text.includes(word));
      item.el.hidden = !hit;
      if (hit) {
        matches += 1;
        matched.push(item.el);
      }
    });

    this.groups.forEach((group) => {
      group.hidden = !group.querySelector('[data-exc-faq-q]:not([hidden])');
    });

    this.browse.forEach((el) => {
      el.hidden = words.length > 0;
    });

    if (words.length && matches > 0 && matches <= 3) {
      matched.forEach((el) => {
        if (!el.open) {
          el.open = true;
          this.autoOpened.push(el);
        }
      });
    }

    if (this.status) {
      const typed = (raw || '').trim();
      if (!words.length) {
        this.status.textContent = '';
      } else if (matches > 0) {
        const noun = matches === 1 ? 'answer' : 'answers';
        this.status.textContent = `${matches} ${noun} for "${typed}"`;
      } else {
        this.status.textContent = `No answers for "${typed}"`;
      }
    }
    if (this.none) this.none.hidden = !(words.length && matches === 0);

    if (updateUrl) {
      const url = new URL(window.location.href);
      if (query) url.searchParams.set('q', query);
      else url.searchParams.delete('q');
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }

  openFromHash() {
    let id = '';
    try {
      id = decodeURIComponent(window.location.hash.slice(1));
    } catch (error) {
      return;
    }
    if (!id) return;
    const target = document.getElementById(id);
    if (!target || !target.matches('[data-exc-faq-q]')) return;
    if (target.hidden && this.input) {
      this.input.value = '';
      this.apply('', true);
    }
    target.open = true;
    target.scrollIntoView({ block: 'start' });
  }
}

if (!customElements.get('exc-faq-search')) {
  customElements.define('exc-faq-search', ExcFaqSearch);
}
